/**
 * PATH: apps/ai/src/pages/ExtractionPage.tsx
 * PURPOSE: Document data extraction interface
 */

import { useState, useCallback } from 'react';
import { Upload, FileText, Image, FileSpreadsheet, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

interface ExtractionResult {
  id: string;
  filename: string;
  type: 'invoice' | 'quote' | 'receipt' | 'document';
  status: 'processing' | 'completed' | 'failed';
  extractedData?: Record<string, unknown>;
  confidence?: number;
  error?: string;
}

export default function ExtractionPage() {
  const [isDragging, setIsDragging] = useState(false);
  const [results, setResults] = useState<ExtractionResult[]>([
    {
      id: '1',
      filename: 'Invoice-2024-001.pdf',
      type: 'invoice',
      status: 'completed',
      confidence: 0.95,
      extractedData: {
        vendor: 'ABC Electrical Supplies',
        invoiceNumber: 'INV-2024-001',
        date: '2024-01-15',
        total: 4250.00,
        items: 12,
      },
    },
    {
      id: '2',
      filename: 'Quote-Plumbing.pdf',
      type: 'quote',
      status: 'completed',
      confidence: 0.88,
      extractedData: {
        vendor: 'XYZ Plumbing Co',
        quoteNumber: 'Q-2024-089',
        date: '2024-01-12',
        total: 8750.00,
        items: 24,
      },
    },
  ]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    processFiles(files);
  };

  const processFiles = (files: File[]) => {
    files.forEach((file) => {
      const newResult: ExtractionResult = {
        id: Date.now().toString() + Math.random(),
        filename: file.name,
        type: 'document',
        status: 'processing',
      };
      
      setResults((prev) => [newResult, ...prev]);
      
      // Simulate processing
      setTimeout(() => {
        setResults((prev) =>
          prev.map((r) =>
            r.id === newResult.id
              ? {
                  ...r,
                  status: 'completed',
                  confidence: 0.85 + Math.random() * 0.1,
                  extractedData: {
                    documentType: 'Invoice',
                    vendor: 'Sample Vendor',
                    total: Math.floor(Math.random() * 10000),
                    date: new Date().toISOString().split('T')[0],
                  },
                }
              : r
          )
        );
      }, 3000);
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'invoice':
        return <FileText className="w-5 h-5" />;
      case 'quote':
        return <FileSpreadsheet className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="flex items-center gap-1 text-green-600 text-sm">
            <CheckCircle className="w-4 h-4" /> Completed
          </span>
        );
      case 'processing':
        return (
          <span className="flex items-center gap-1 text-orange-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Processing
          </span>
        );
      case 'failed':
        return (
          <span className="flex items-center gap-1 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" /> Failed
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Data Extraction</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Extract structured data from documents, images, and PDFs using AI
        </p>
      </div>

      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "border-2 border-dashed rounded-xl p-12 text-center transition-all",
          isDragging
            ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
            : "border-gray-300 dark:border-gray-600 hover:border-orange-400"
        )}
      >
        <Upload className={cn(
          "w-12 h-12 mx-auto mb-4",
          isDragging ? "text-orange-500" : "text-gray-400"
        )} />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Drop files here to extract data
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          Supports PDF, images (PNG, JPG), and spreadsheets (XLSX, CSV)
        </p>
        <label className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer transition-colors">
          <Upload className="w-4 h-4" />
          Select Files
          <input
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.xlsx,.csv"
            onChange={handleFileSelect}
            className="hidden"
          />
        </label>
      </div>

      {/* Supported Formats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: FileText, title: 'Documents', formats: 'PDF, Word', desc: 'Invoices, quotes, contracts' },
          { icon: Image, title: 'Images', formats: 'PNG, JPG', desc: 'Photos, scans, screenshots' },
          { icon: FileSpreadsheet, title: 'Spreadsheets', formats: 'Excel, CSV', desc: 'Price lists, data exports' },
        ].map((item, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <item.icon className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">{item.title}</h4>
                <p className="text-xs text-gray-500">{item.formats}</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{item.desc}</p>
          </div>
        ))}
      </div>

      {/* Results */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="font-semibold text-gray-900 dark:text-white">Extraction Results</h2>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {results.map((result) => (
            <div key={result.id} className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    {getTypeIcon(result.type)}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">{result.filename}</h4>
                    <p className="text-sm text-gray-500 capitalize">{result.type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {result.confidence && (
                    <span className="text-sm text-gray-500">
                      {(result.confidence * 100).toFixed(0)}% confidence
                    </span>
                  )}
                  {getStatusBadge(result.status)}
                </div>
              </div>
              
              {result.status === 'completed' && result.extractedData && (
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mt-3">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Extracted Data</h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(result.extractedData).map(([key, value]) => (
                      <div key={key}>
                        <p className="text-xs text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1')}</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {typeof value === 'number' && key.toLowerCase().includes('total')
                            ? `$${value.toLocaleString()}`
                            : String(value)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

